/**
 * Authors: mariushirjanu
 * Created: 27/05/2021
 */
import { NgModule } from '@angular/core';
import { RedirectToResult, Transition } from '@uirouter/angular';
import { UIRouterModule } from '@uirouter/angular';
import { SharedModule } from '@app/shared/shared.module';
import { CommonModule } from '@angular/common';
import { FaqListComponent } from './list/faq-list.component';
import { STATE_FAQ, STATE_FAQ_CREATE, STATE_FAQ_EDIT, STATE_FAQ_ITEM, STATE_FAQS } from '@app/shared/app-states';
import { FaqListContentComponent } from './list/faq-list-content.component';
import { FaqItemComponent } from '@app/shared/faq/faq-item.component';
import { GaStateDeclaration } from '@type_definitions/datatypes';
import { FaqSearchComponent } from './list/faq-search.component';
import { FaqManageComponent } from '@app/main/faq/manage/faq-manage.component';
import { FaqManageDocumentsComponent } from '@app/main/faq/manage/faq-manage-documents.component';
import { FaqManageDialogComponent } from '@app/main/faq/manage/faq-manage-dialog.component';

const faqUrlReplace = (trans:Transition):RedirectToResult => {
  const faqId:string = trans.params().faqId;
  if (/[^\d\?]+/.test(faqId)) {
    // redirect /faqs/SUPPORT to /faqs?keyword=SUPPORT
    return {
      state: STATE_FAQS,
      params: { keyword: faqId }
    };
  } else {
    // redirect /faqs/37 to /faq/37
    return {
      state: STATE_FAQ,
      params: { key: faqId }
    };
  }
};

export const states:GaStateDeclaration[] = [
  {
    name: STATE_FAQS,
    url: '/faqs?keyword&webshop&title&query&autoload',
    component: FaqListComponent,
    resolve: [
      {
        token: 'keyword',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().keyword
      },
      {
        token: 'webshop',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().webshop
      },
      {
        token: 'title',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().title
      },
      {
        token: 'query',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().query
      },
      {
        token: 'autoload',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().autoload
      }
    ],
    bindings: {
      keyword: 'keyword',
      webshop: 'webshop',
      title: 'title',
      query: 'query',
      autoload: 'autoload'
    },
    params: {
      query: { value: null }
    },
    data: {
      viewTitle: 'FAQ'
    }
  },
  {
    name: 'main.faqsearch',
    url: '/faqsearch?question',
    params: {
      question: {
        dynamic: true
      }
    },
    component: FaqSearchComponent,
    resolve: [
      {
        token: 'question',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().question
      }
    ]
  },
  { // supports getting FAQ by faqId or faqCode
    name: STATE_FAQ,
    url: '/faq/:key',
    component: FaqItemComponent,
    resolve: [
      {
        token: 'faqId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => !/[^\d\?]+/.test(trans.params().key)? trans.params().key : undefined
      },
      {
        token: 'faqCode',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => /[^\d\?]+/.test(trans.params().key)? trans.params().key : undefined
      },
      {
        token: 'onFaqPage',
        resolveFn: () => true
      }
    ],
    bindings: {
      faqId: 'faqId',
      faqCode: 'faqCode'
    },
    data: {
      viewTitle: 'FAQ'
    }
  },
  { // supports getting FAQ only by faqId; when faqId is string -> redirect to search page
    name: STATE_FAQ_ITEM,
    url: '/faqs/:faqId',
    component: FaqItemComponent,
    resolve: [
      {
        token: 'faqId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().faqId
      },
      {
        token: 'onFaqPage',
        resolveFn: () => true
      }
    ],
    redirectTo: faqUrlReplace,
    bindings: {
      faqId: 'faqId'
    },
    data: {
      viewTitle: 'FAQ'
    }
  },
  // FAQ edit page
  {
    name: STATE_FAQ_EDIT,
    url: '/faq/:key/edit',
    component: FaqManageComponent,
    resolve: [
      {
        token: 'faqId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => !/[^\d\?]+/.test(trans.params().key)? trans.params().key : undefined
      },
      {
        token: 'faqCode',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => /[^\d\?]+/.test(trans.params().key)? trans.params().key : undefined
      }
    ],
    bindings: {
      faqId: 'faqId',
      faqCode: 'faqCode'
    },
    data: {
      viewTitle: 'Edit FAQ'
    }
  },
  // FAQ add page
  {
    name: STATE_FAQ_CREATE,
    url: '/faq/NEW?code',
    component: FaqManageComponent,
    resolve: [
      {
        token: 'faqCode',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => trans.params().code
      },
      {
        token: 'create',
        resolveFn: () => true
      }
    ],
    data: {
      viewTitle: 'New FAQ'
    },
    bindings: {
      faqCode: 'faqCode',
      create: 'create'
    }
  }
];

@NgModule({
  imports: [
    UIRouterModule.forChild({ states }),
    CommonModule,
    SharedModule
  ],
  declarations: [
    FaqListComponent,
    FaqListContentComponent,
    FaqSearchComponent,
    FaqManageComponent,
    FaqManageDialogComponent,
    FaqManageDocumentsComponent
  ]
})
export class FaqModule {
}